import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import {
  ExportType,
  getMetricPayload,
  MetricKey,
  MetricsFacade,
  MetricStatistic,
  MetricView,
} from '@app/shared/+state/metrics';
import { MetricColour } from '@app/shared/components/metric-widget-mono/metric-widget.component.interface';
import { combineLoadingStates, LoadingState, User } from '@app/shared/interfaces';
import { Filter } from '@app/shared/interfaces/lib/filter.interface';
import { DonutChart } from '@app/ui/donut-chart/donut-chart.component.interface';
import { TableData } from '@app/ui/table/table.component.interface';
import { combineLatest, Observable, Subscription } from 'rxjs';
import { map, take } from 'rxjs/operators';
import {
  ExportMetricsDialogComponent,
  ExportMetricsDialogPayload,
} from '../export-metrics-dialog/export-metrics-dialog.component';
import { ListViewDialogComponent } from '../list-view-dialog/list-view-dialog.component';

@Component({
  selector: `hospitable-metrics-reservations`,
  templateUrl: './reservations.component.html',
  styleUrls: ['./reservations.component.scss', './reservations.component.mobile.scss'],
})
export class ReservationsComponent implements OnInit, OnDestroy {
  @Input() user: User | null;

  pageLoadingState$: Observable<LoadingState | null>;
  LoadingState = LoadingState;
  MetricColour = MetricColour;

  filtersLoadingState$: Observable<LoadingState | null>;
  filters$: Observable<Filter[] | null>;
  appliedFilters$: Observable<Filter[] | null>;

  dataTooLarge$: Observable<boolean>;

  statistics$: Observable<MetricStatistic[] | null>;
  graphs$: Observable<DonutChart[] | null>;

  detailsLoadingState$: Observable<LoadingState | null>;
  details$: Observable<TableData | null>;

  count: number | null;
  countSubscription$: Subscription;

  views$: Observable<MetricView[] | null>;
  activeView$: Observable<MetricView | null>;

  MetricKey = MetricKey;

  constructor(
    private metricsFacade: MetricsFacade,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.filtersLoadingState$ = this.metricsFacade.reservationFiltersLoadingState$;
    this.detailsLoadingState$ = this.metricsFacade.reservationDetailsLoadingState$;
    this.filters$ = this.metricsFacade.reservationFilters$;
    this.appliedFilters$ = this.metricsFacade.reservationAppliedFilters$;
    this.statistics$ = this.metricsFacade.reservationStatistics$;
    this.graphs$ = this.metricsFacade.reservationGraphs$;
    this.details$ = this.metricsFacade.reservationDetails$;
    this.dataTooLarge$ = this.metricsFacade.reservationDataTooLarge$;
    this.countSubscription$ = this.metricsFacade.reservationCount$.subscribe((count) => (this.count = count));
    this.activeView$ = this.metricsFacade.activeReservationView$;

    this.views$ = this.metricsFacade.reservationViews$;
    this.pageLoadingState$ = combineLatest(
      this.metricsFacade.reservationStatisticsLoadingState$,
      this.metricsFacade.reservationGraphsLoadingState$,
      this.metricsFacade.reservationViewsLoadingState$
    ).pipe(
      map(([reservationStatisticsLoadingState, reservationGraphsLoadingState, reservationViewsLoadingState]) =>
        combineLoadingStates([
          reservationStatisticsLoadingState,
          reservationGraphsLoadingState,
          reservationViewsLoadingState,
        ])
      )
    );

    this.metricsFacade.loadReservationFilters();
    this.metricsFacade.loadReservationViews();
  }

  ngOnDestroy() {
    if (this.countSubscription$ && this.countSubscription$.unsubscribe) {
      this.countSubscription$.unsubscribe();
    }
  }

  onAddFilter(filter: Filter) {
    this.metricsFacade.addReservationFilter(filter);
  }

  onApplyFilter(filter: Filter) {
    this.metricsFacade.applyReservationFilter(filter);
  }

  onRemoveFilter(filter: Filter) {
    this.metricsFacade.removeReservationFilter(filter);
  }

  onClearAllFilters() {
    console.log('onClearAllFilters=> ');
  }

  onDetailsPageChange(page: number, appliedFilters: Filter[] | null) {
    const payload = getMetricPayload(appliedFilters);
    this.metricsFacade.loadReservationDetails(payload, page);
  }

  export(appliedFilters: Filter[] | null) {
    const payload = getMetricPayload(appliedFilters);
    if (this.user) {
      const dialogRef = this.dialog.open(ExportMetricsDialogComponent, {
        width: '720px',
        maxHeight: '90vh',
        data: {
          email: this.user.email,
          rowCount: this.metricsFacade.reservationCount$,
        },
      });

      let exportType: ExportType;

      this.metricsFacade.loadReservationCount(false);

      const dialogSubscription$ = dialogRef.componentInstance.exportTypeUpdated.subscribe(
        (exportTypeSelected: ExportType) => {
          if (exportTypeSelected) {
            exportType = exportTypeSelected;
            this.metricsFacade.loadReservationCount(exportType === ExportType.Multiple ? true : false);
          }
        }
      );

      const dialogClosedSubscription$ = dialogRef.componentInstance.dialogClosed.subscribe(
        (dialogPayload: ExportMetricsDialogPayload) => {
          if (dialogPayload) {
            const { exportType, includeKPIs } = dialogPayload;

            this.metricsFacade.exportReservationMetrics({
              ...payload,
              ...{
                one_file_per_property: exportType === ExportType.Multiple ? true : false,
                include_kpis: includeKPIs,
              },
            });
          }
        }
      );

      dialogRef.afterClosed().subscribe(() => {
        dialogSubscription$ && dialogSubscription$.unsubscribe ? dialogSubscription$.unsubscribe() : null;
        dialogClosedSubscription$ && dialogClosedSubscription$.unsubscribe
          ? dialogClosedSubscription$.unsubscribe()
          : null;
      });
    }
  }
  openListViewDialog(views: MetricView[]) {
    this.activeView$.pipe(take(1)).subscribe((view) => {
      this.dialog.open(ListViewDialogComponent, {
        width: '720px',
        maxHeight: '90vh',
        data: {
          metricKey: MetricKey.Reservations,
          views,
          activeView: view,
        },
      });
    });
  }

  clearActiveView(event: any) {
    event.stopPropagation();
    this.metricsFacade.clearActiveReservationView();
  }
}
